import React, { useState, useEffect, useRef } from "react";
import SimpleImageSlider from "react-simple-image-slider";
import apiUrl from '../utility/apiUrl';
import "../css/CarouselBanner.css";

// 16:9 aspect ratio height so banner images are shown in full without cropping.
// Cap at 480px on large screens to avoid an oversized hero.
const getResponsiveHeight = (width) => {
  if (width <= 0) return 240;
  return Math.min(Math.round(width * (9 / 16)), 480);
};

// Nav arrow size: 6% of container width, clamped between 24px (mobile) and 50px (desktop).
const getNavSize = (width) => Math.max(24, Math.min(50, Math.round(width * 0.06)));

// Nav margin: 2% of container width, clamped between 4px and 20px.
const getNavMargin = (width) => Math.max(4, Math.min(20, Math.round(width * 0.02)));

// react-simple-image-slider paints each slide as a CSS background-image, which
// never fires a DOM error event on a dead URL — it just renders as solid black.
// Preload each candidate and drop the ones that fail before handing them to the
// slider, so a stale/expired banner URL degrades to "not shown" instead of black.
const preloadableImage = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });

const CarouselBanner = () => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [imageData, setImageData] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchBannerImagesData = async () => {
      try {
        const response = await fetch(apiUrl + '/api/v1/banner/list', {
          headers: {
            'Content-Type': 'application/json',
            "ngrok-skip-browser-warning": "any"
          },
          method: 'POST'
        });
        const data = await response.json();
        const activeBanners = Array.isArray(data)
          ? data.filter((b) => b.isActive === 1 && b.url)
          : [];
        if (activeBanners.length === 0) return;

        const loadable = await Promise.all(
          activeBanners.map(async (b) => ((await preloadableImage(b.url)) ? b : null))
        );
        const workingBanners = loadable.filter(Boolean);
        if (cancelled || workingBanners.length === 0) return;

        setTimeout(() => {
          if (!cancelled) setImageData(workingBanners);
        }, 500);
      } catch (error) {
        console.error('Error fetching banners:', error);
      }
    };

    fetchBannerImagesData();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const sliderHeight = getResponsiveHeight(containerWidth);
  const navSize = getNavSize(containerWidth);
  const navMargin = getNavMargin(containerWidth);

  return (
    <div
      ref={containerRef}
      className="carousel-banner-wrapper"
    >
      {containerWidth > 0 && imageData.length > 0 && (
        <SimpleImageSlider
          width={containerWidth}
          height={sliderHeight}
          images={imageData}
          showBullets={true}
          showNavs={true}
          autoPlay={true}
          autoPlayDelay={3}
          navSize={navSize}
          navMargin={navMargin}
        />
      )}
    </div>
  );
};

export default CarouselBanner;
